<template>
    <div
        class="flex items-center justify-between gap-4 rounded-b-lg border-b border-l border-r border-t-0 border-ds-primary-200 px-4 py-3 text-content-md xs:flex-col lg:flex-row"
    >
        <span class="min-w-40 whitespace-nowrap text-ds-primary-700">
            Showing {{ startItem }}-{{ endItem }} of {{ totalRecords }}
        </span>

        <div class="flex flex-1 items-center justify-center gap-1">
            <DsIconButton
                :class="{ invisible: page === 0 }"
                icon="fa-solid fa-chevron-left"
                size="sm"
                tooltipText="Previous page"
                @click="handlePreviousPage"
            />
            <div class="mr-1"><!-- spacer --></div>
            <template
                v-for="(pageOption, index) in visiblePages"
                :key="index"
            >
                <DsTablePaginationButton
                    v-if="pageOption.ellipsis"
                    disabled
                >
                    <DsIcon
                        icon="fa-solid fa-ellipsis"
                        size="sm"
                    />
                </DsTablePaginationButton>
                <DsTablePaginationButton
                    v-else
                    :selected="page === pageOption.offset"
                    size="sm"
                    @click="handlePageClick(pageOption)"
                >
                    <div class="flex h-5 min-w-5 items-center justify-center text-content-sm">
                        {{ pageOption.text }}
                    </div>
                </DsTablePaginationButton>
            </template>
            <div class="ml-1"><!-- spacer --></div>
            <DsIconButton
                :class="{ invisible: page >= totalPages - 1 }"
                icon="fa-solid fa-chevron-right"
                size="sm"
                tooltipText="Next page"
                @click="handleNextPage"
            />
        </div>

        <div class="flex items-center gap-2 text-ds-gray-700">
            <label
                class="mb-0 whitespace-nowrap text-sm text-content-secondary"
                :for="id"
                >Results per page:</label
            >
            <select
                v-model.number="rows"
                :id
                class="w-14 rounded border border-ds-primary-300 px-2 py-1 text-xs"
            >
                <option :value="10">10</option>
                <option :value="25">25</option>
                <option :value="50">50</option>
                <option :value="100">100</option>
            </select>
        </div>
    </div>
</template>

<script setup lang="ts">
import DsIcon from '@/components/design-system/DsIcon.vue';
import DsIconButton from '@/components/design-system/DsIconButton.vue';
import DsTablePaginationButton from '@/components/design-system/table/DsTablePaginationButton.vue';
import { computed, useId } from 'vue';

const props = defineProps<{
    first: number;
    totalRecords: number;
}>();

const page = defineModel<number>('page', { required: true });
const rows = defineModel<number>('rows', { required: true });
const totalPages = computed(() => Math.ceil(props.totalRecords / rows.value));
const id = useId();
const startItem = computed(() => {
    if (props.totalRecords === 0) return 0;
    return props.first + 1;
});

const endItem = computed(() => {
    return Math.min(props.first + rows.value, props.totalRecords);
});

type PageOption = {
    offset?: number;
    text: number | string;
    ellipsis?: boolean;
};

const visiblePages = computed((): PageOption[] => {
    const pages: PageOption[] = [];
    const total = totalPages.value;
    if (total === 0) return [];

    const SHOW_ALL_PAGES_THRESHOLD = 8;
    const PAGES_AROUND_CURRENT = 1;
    const EDGE_ZONE_PAGE_COUNT = 3;

    const currentPageIndex = page.value;
    const ELLIPSIS: PageOption = { text: '', ellipsis: true };

    if (total <= SHOW_ALL_PAGES_THRESHOLD) {
        // If total pages are few, show all page numbers
        for (let i = 0; i < total; i++) {
            pages.push({ offset: i, text: i + 1 });
        }
    } else {
        pages.push({ offset: 0, text: 1 });

        // Add left ellipsis if the current page is past the edge zone
        if (currentPageIndex > EDGE_ZONE_PAGE_COUNT) {
            pages.push(ELLIPSIS);
        }

        // Determine the set of middle pages to display
        if (currentPageIndex < EDGE_ZONE_PAGE_COUNT) {
            // Case 1: Current page is near the beginning
            for (let i = 1; i < EDGE_ZONE_PAGE_COUNT + 1; i++) {
                pages.push({ offset: i, text: i + 1 });
            }
        } else if (currentPageIndex >= total - EDGE_ZONE_PAGE_COUNT) {
            // Case 2: Current page is near the end
            for (let i = total - (EDGE_ZONE_PAGE_COUNT + 1); i < total - 1; i++) {
                pages.push({ offset: i, text: i + 1 });
            }
        } else {
            // Case 3: Current page is in the middle
            for (let i = currentPageIndex - PAGES_AROUND_CURRENT; i <= currentPageIndex + PAGES_AROUND_CURRENT; i++) {
                pages.push({ offset: i, text: i + 1 });
            }
        }

        // Add right ellipsis if the current page is before the end edge zone
        if (currentPageIndex < total - (EDGE_ZONE_PAGE_COUNT + 1)) {
            pages.push(ELLIPSIS);
        }

        pages.push({ offset: total - 1, text: total });

        // Remove duplicates that might occur at the edges
        return pages.filter(
            (page, index, self) => index === self.findIndex(p => p.offset === page.offset && p.text === page.text),
        );
    }

    return pages;
});

const handlePreviousPage = () => {
    if (page.value > 0) {
        page.value--;
    }
};

const handleNextPage = () => {
    if (page.value < totalPages.value - 1) {
        page.value++;
    }
};

const handlePageClick = (pageOption: PageOption) => {
    if (pageOption.offset !== undefined && pageOption.offset !== page.value) {
        page.value = pageOption.offset;
    }
};
</script>
